import { useAuth } from "@clerk/clerk-react";
import { Box, Skeleton } from "@mui/material";
import { useGoogleLogin } from "@react-oauth/google";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useState } from "react";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

interface GoogleConnectProps {
  onSuccessAction?: () => Promise<void>;
  fullWidth?: boolean;
  styles?: any;
}

export default function GoogleConnect({
  onSuccessAction,
  fullWidth,
  styles,
}: GoogleConnectProps) {
  const { getToken } = useAuth();
  const { setAlert } = useContext(AlertContext);
  const { currentOrg, currentUser, setUserIntegrations } = useContext(
    OrganizationUserContext,
  );
  const { setScopes, setEmailHealth, userIntegration, setUserIntegration } =
    useContext(UserIntegrationsContext);

  const [loading, setLoading] = useState<boolean>(false);

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async (tokenResponse) => {
      if (!currentOrg) return;
      try {
        setLoading(true);

        const res = await fetcherAuth(
          getToken,
          `/api/oauth/google/${currentOrg?.id}/callback`,
          "POST",
          {},
          tokenResponse,
        );

        setScopes(res.scopes);
        setEmailHealth(res.emailHealth);
        setUserIntegration(res.userIntegration);
        setUserIntegrations(res.userIntegrations);
        if (onSuccessAction && res.emailHealth) {
          await onSuccessAction();
        }
      } catch (error) {
        setAlert(
          error.message ? error.message : "Unable to connect with Google",
          "error",
        );
      } finally {
        setLoading(false);
      }
    },
    onError: () => {
      setAlert("Unable to connect with Google", "error");
      setLoading(false);
    },
    hint: currentUser?.email,
    scope: userIntegration?.isNegotiationsEmail
      ? "https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/gmail.compose https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.readonly"
      : "https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.readonly",
  });

  const handleConnectToGoogle = () => {
    trackEvent("Connect To Google Button Clicked");
    googleLogin();
  };

  return loading ? (
    <Skeleton
      variant="rectangular"
      width={"100%"}
      height={40}
      sx={{ borderRadius: 2 }}
    />
  ) : (
    <StyledLargeButton
      variant="outlined"
      onClick={handleConnectToGoogle}
      fullWidth={fullWidth}
      styles={styles}
      startIcon={
        <Box
          component="i"
          className="fa-brands fa-google"
          sx={{ fontSize: 16 }}
        />
      }
    >
      Connect to Google
    </StyledLargeButton>
  );
}
